import {
  Children,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { Button, Icons } from "@/components/ui";
import type { IconName } from "@/components/ui/icons";
import { restoreAndFocusCurrentWindow } from "@/desktop/use-global-hotkey";
import { useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { cn } from "@/lib/cn";
import { REVEAL_EASE, REVEAL_MS } from "@/lib/reveal-motion";
import "./toast-motion.css";
import { isTauri } from "@/lib/is-tauri";
import { ToastMoreMenu, type ToastMenuItem } from "./toast-more-menu";
import { toastShellAttributes, toastShellClassName } from "./toast-pill";

export interface ToastCardProps {
  toastKey: string;
  state: string;
  logo: ReactNode;
  title: string;
  subtitle: string;
  subtitleIcon?: IconName;
  menuItems: ToastMenuItem[];
  onMenuOpenChange?: (open: boolean) => void;
  onCollapse: () => void;
  collapseLabel?: string;
  /** Compact header action used when the conversation is embedded in the tray. */
  onOpenChat?: () => void;
  /** Open the app destination from an expanded tray work toast. */
  onOpenApp?: () => void;
  /** Stop the run; offered only while it is running and can be stopped. */
  onStop?: () => void;
  /** Expanding is a user action that must bring the desktop window forward
   *  (spec §4.3). False for a card that opened by itself: it takes the
   *  keyboard only once the user presses inside it. */
  focusOnMount?: boolean;
  /** When set and mounted, this wins initial focus over the body's first
   *  focusable control (e.g. a reply textarea opened via "T" or a mic). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** Render as content within a parent glass surface. */
  embedded?: boolean;
}

/** The expanded card (spec §4.3, same width as the pill so expansion never
 *  changes widths: `--denker-toast-width`). Holds a question, approval, result, or
 *  long system message under the same glass shell and header pattern as the
 *  pill. Esc and the collapse button both hand control back to `onCollapse`. */
export function ToastCard({
  toastKey,
  state,
  logo,
  title,
  subtitle,
  subtitleIcon,
  menuItems,
  onMenuOpenChange,
  onCollapse,
  collapseLabel,
  onOpenChat,
  onOpenApp,
  onStop,
  focusOnMount = true,
  initialFocusRef,
  children,
  embedded = false,
}: ToastCardProps) {
  const t = useT(toastsMessages);
  const collapseText = collapseLabel ?? t("collapse");
  const onCollapseRef = useRef(onCollapse);
  onCollapseRef.current = onCollapse;
  const focusOnMountRef = useRef(focusOnMount);
  const initialFocusRefHolder = useRef(initialFocusRef);
  initialFocusRefHolder.current = initialFocusRef;
  const cardRef = useRef<HTMLDivElement>(null);
  const SubtitleIcon = subtitleIcon ? Icons[subtitleIcon] : null;
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnMountRef.current) return;
    if (isTauri()) restoreAndFocusCurrentWindow();
  }, []);

  // Expanding replaces the pill with this card, so nothing inside it has
  // focus yet — without this, Esc does nothing until the user tabs in
  // manually (the Esc listener below only acts while focus is inside the
  // card). Per spec §4.3, initial focus belongs to `initialFocusRef` when
  // the caller supplied one and it's actually mounted (e.g. a reply
  // textarea the card was opened specifically to fill — see
  // work-toast-pill.tsx's "reply" open path); otherwise it falls back to
  // the body's own first focusable control — never the header's ⋮ or
  // Collapse, which would otherwise win just by coming first in DOM order.
  // Falls back further to the card root itself (tabIndex={-1} below) when
  // the body has nothing focusable, so Esc still works immediately either
  // way. This is the single focus mechanism for the card: callers must not
  // layer a second one (e.g. a counter-driven effect) on top of it.
  // Gated on the same `focusOnMount` flag as the window raise above.
  useEffect(() => {
    if (!focusOnMountRef.current) return;
    const target =
      initialFocusRefHolder.current?.current ??
      bodyRef.current?.querySelector<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
      ) ??
      cardRef.current;
    // Never scroll: the card opens inside an already-visible rail column and
    // focusing must not yank the column (or the page) to the card.
    target?.focus({ preventScroll: true });
  }, []);

  // A card that opened without the keyboard gets it from the user's own press:
  // the overlay window does not take the keyboard on a click by itself.
  const takeKeyboardOnPress = () => {
    if (isTauri() && !document.hasFocus()) restoreAndFocusCurrentWindow();
  };

  // The card is a non-blocking floating overlay, so a document-wide Escape
  // listener would also fire for an unrelated dropdown, a rename field, or
  // any other input elsewhere on the page — discarding whatever the user was
  // doing there. Only collapse (and only claim the event) when focus is
  // actually inside this card.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (!cardRef.current?.contains(document.activeElement)) return;
      event.preventDefault();
      onCollapseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div
      ref={cardRef}
      className={cn(
        !embedded && toastShellClassName,
        "denker-toast-card relative flex shrink-0 flex-col gap-2.5 p-3 outline-none",
        embedded ? "w-full rounded-[16px]" : "denker-toast-width",
      )}
      style={
        {
          "--denker-toast-open": `${REVEAL_MS}ms`,
          "--denker-toast-ease": REVEAL_EASE,
        } as CSSProperties
      }
      data-toast-embedded={embedded || undefined}
      // Only a fallback focus target when the body has nothing focusable
      // (see the mount effect above) — not part of the normal tab order.
      tabIndex={-1}
      data-testid="toast-card"
      onPointerDownCapture={takeKeyboardOnPress}
      data-interactive="true"
      data-toast-key={toastKey}
      data-toast-state={state}
      {...(embedded ? {} : toastShellAttributes)}
    >
      <div className="flex items-center gap-2.5">
        {logo}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-appkit-body font-medium text-primary">
            {title}
          </span>
          {subtitle && (
            <span className="flex min-w-0 items-center gap-1 truncate text-appkit-small font-medium text-muted">
              {SubtitleIcon && (
                <SubtitleIcon
                  className="h-3 w-3 shrink-0"
                  aria-hidden="true"
                  data-testid="toast-card-subtitle-icon"
                />
              )}
              <span className="truncate">{subtitle}</span>
            </span>
          )}
        </span>
        <span className="flex items-center gap-0.5">
          {onStop && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mr-1"
              aria-label={t("stop")}
              title={t("stop")}
              onClick={onStop}
              data-testid="toast-card-stop"
              data-interactive="true"
            >
              {t("stop")}
            </Button>
          )}
          {menuItems.length > 0 && (
            <ToastMoreMenu
              items={menuItems}
              testId="toast-card-menu"
              tray={embedded}
              onOpenChange={onMenuOpenChange}
            />
          )}
          {onOpenApp ? (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              aria-label={t("openApp")}
              title={t("openApp")}
              onClick={onOpenApp}
              data-testid="toast-card-open-app"
              data-interactive="true"
            >
              <Icons.ArrowUpRight className="h-4 w-4" />
            </Button>
          ) : onOpenChat ? (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              aria-label={t("openChat")}
              title={t("openChat")}
              onClick={onOpenChat}
              data-testid="toast-card-open-chat"
              data-interactive="true"
            >
              <Icons.ArrowUpRight className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={collapseText}
            title={collapseText}
            onClick={onCollapse}
            data-testid="toast-card-collapse"
            data-interactive="true"
          >
            <Icons.ChevronUp className="h-4 w-4" />
          </Button>
        </span>
      </div>
      {/* A header-only card has no body wrapper, so no trailing gap. */}
      {Children.toArray(children).length > 0 && (
        <div ref={bodyRef} className="flex flex-col gap-2 px-1">
          {children}
        </div>
      )}
    </div>
  );
}
